/*
 * `/notifications` — one person's notification switches.
 *
 * Reached from the account menu (your own) and from the Users row action
 * (`?userId=<id>`, someone else's). There is no route guard: the server answers
 * a 403 for another person unless the caller holds `users.update` (or is a
 * super admin), and that refusal is rendered as the page.
 *
 * Saving is a WHOLE-SET PUT of this person's override rows plus the changed
 * settings keys. Rules for resolving, counting and writing rows live in
 * `components/notifications/preferences/model.ts`; keep them there.
 *
 * Locked events (every `security` event, some system events, company approval
 * requests) are always on and instant; their controls are disabled with the
 * reason on a wrapper tooltip.
 *
 * No access (owner 2026-10-22): an event whose `effective.access` is `none` is
 * shown off with disabled controls ("<Person>'s permission set does not include
 * <Module>."). A module with every event no access carries a "No access" badge
 * and sorts after the accessible modules. Group switches skip those events. A
 * save refused with 422 `NOTIFICATION_NO_ACCESS` names the modules from the
 * refused row indexes.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Button,
  ConfirmDialog,
  EmptyState,
  Input,
  PageHeader,
  Panel,
  SegmentedControl,
  Skeleton,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { SearchIcon } from '@/components/catalog/icons';
import { ModuleCard } from '@/components/notifications/preferences/ModuleCard';
import { SettingsStrip } from '@/components/notifications/preferences/SettingsStrip';
import {
  draftFromRows,
  isNoAccess,
  moduleHasRows,
  moduleNoAccess,
  noAccessReason,
  rowsFromDraft,
  sameView,
  setEventField,
  setGroupChannel,
  settingsPatch,
  viewOf,
  withoutModule,
  type ChannelField,
  type Draft,
  type EventView,
} from '@/components/notifications/preferences/model';
import { api, ApiError } from '@/lib/api';
import type {
  NotificationCatalogue,
  NotificationEventInfo,
  NotificationModule,
  NotificationPreferenceRow,
  NotificationPreferencesInput,
  NotificationPreferencesResponse,
  NotificationUserSettings,
} from '@/lib/types';
import { useAuth } from '@/lib/use-auth';

type FilterMode = 'all' | 'changed';

const FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'changed', label: 'Changed by me' },
] as const;

function errorText(error: unknown): string {
  if (error instanceof ApiError) return error.formMessage ?? error.message;
  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
}

/** The catalogue module a stored row targets (`<module>`, `<module>.<sub>`, or an event key). */
function moduleOfRow(catalogue: NotificationCatalogue, row: NotificationPreferenceRow): NotificationModule | null {
  for (const mod of catalogue.modules) {
    if (row.targetType === 'module' && row.targetKey === mod.key) return mod;
    for (const sub of mod.submodules) {
      if (row.targetType === 'submodule' && row.targetKey === `${mod.key}.${sub.key}`) return mod;
      if (row.targetType === 'event' && sub.events.some((ev) => ev.key === row.targetKey)) return mod;
    }
  }
  return null;
}

/**
 * 422 `NOTIFICATION_NO_ACCESS`: `details[].path` is the row index into the
 * sent `preferences` (a bare index or `preferences.<n>`). Names the modules.
 */
function noAccessSaveText(
  error: ApiError,
  rows: readonly NotificationPreferenceRow[],
  catalogue: NotificationCatalogue,
  personName: string | null,
): string {
  const labels = new Set<string>();
  for (const detail of error.details ?? []) {
    const match = /(\d+)(?!.*\d)/.exec(detail.path);
    const row = match ? rows[Number(match[1])] : undefined;
    const mod = row ? moduleOfRow(catalogue, row) : null;
    if (mod) labels.add(mod.label);
  }
  if (labels.size === 0) return error.formMessage ?? error.message;
  return noAccessReason([...labels].join(', '), personName);
}

function computeViews(
  catalogue: NotificationCatalogue,
  saved: NotificationPreferencesResponse,
  draft: Draft,
): Map<string, EventView> {
  const views = new Map<string, EventView>();
  for (const mod of catalogue.modules) {
    for (const sub of mod.submodules) {
      for (const ev of sub.events) views.set(ev.key, viewOf(draft, saved, mod, sub, ev));
    }
  }
  return views;
}

/* Keyed on `?userId=` so moving between people starts from a clean slate. */
export function NotificationsPage() {
  const [searchParams] = useSearchParams();
  return <NotificationsEditor key={searchParams.get('userId') ?? 'self'} />;
}

function NotificationsEditor() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const rawUserId = searchParams.get('userId');
  const parsedUserId = rawUserId === null ? null : /^\d+$/.test(rawUserId) ? Number(rawUserId) : Number.NaN;
  const invalidUserId = parsedUserId !== null && Number.isNaN(parsedUserId);
  /* Your own id in the link is the ordinary page. */
  const targetUserId = parsedUserId !== null && user && parsedUserId === user.id ? null : parsedUserId;

  const [catalogue, setCatalogue] = useState<NotificationCatalogue | null>(null);
  const [saved, setSaved] = useState<NotificationPreferencesResponse | null>(null);
  const [draft, setDraft] = useState<Draft>(new Map());
  const [settings, setSettings] = useState<NotificationUserSettings | null>(null);
  const [targetName, setTargetName] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [resetTarget, setResetTarget] = useState<NotificationModule | null>(null);
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  const [openModules, setOpenModules] = useState<ReadonlySet<string>>(new Set());
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterMode>('all');

  /* `null` = your own page; names the person in no-access reasons otherwise. */
  const personName = targetUserId === null ? null : (targetName ?? 'This person');

  useEffect(() => {
    if (invalidUserId) return;
    const controller = new AbortController();
    const { signal } = controller;

    void (async () => {
      try {
        const [catalogueResponse, preferencesResponse] = await Promise.all([
          api.getNotificationCatalogue(signal),
          api.getNotificationPreferences(targetUserId, signal),
        ]);
        if (signal.aborted) return;
        setCatalogue(catalogueResponse.data);
        setSaved(preferencesResponse.data);
        setDraft(draftFromRows(preferencesResponse.data.preferences));
        setSettings(preferencesResponse.data.settings);
        setSaveError(null);
      } catch (error) {
        if (signal.aborted) return;
        setLoadError(errorText(error));
      }
    })();

    if (targetUserId !== null) {
      void api
        .listAllUsers({}, signal)
        .then((response) => {
          if (signal.aborted) return;
          setTargetName(response.data.find((row) => row.id === targetUserId)?.name ?? null);
        })
        .catch(() => undefined);
    }

    return () => controller.abort();
  }, [targetUserId, invalidUserId, reloadToken]);

  const views = useMemo(
    () => (catalogue && saved ? computeViews(catalogue, saved, draft) : new Map<string, EventView>()),
    [catalogue, saved, draft],
  );
  /* Event keys forced off because the person cannot read their module. */
  const noAccessKeys = useMemo(() => {
    const keys = new Set<string>();
    if (!catalogue || !saved) return keys;
    for (const mod of catalogue.modules) {
      for (const sub of mod.submodules) {
        for (const ev of sub.events) if (isNoAccess(saved, ev)) keys.add(ev.key);
      }
    }
    return keys;
  }, [catalogue, saved]);
  const savedViews = useMemo(
    () =>
      catalogue && saved
        ? computeViews(catalogue, saved, draftFromRows(saved.preferences))
        : new Map<string, EventView>(),
    [catalogue, saved],
  );

  const patch = saved && settings ? settingsPatch(saved.settings, settings) : null;

  const changeCount = useMemo(() => {
    if (!catalogue) return 0;
    let count = 0;
    for (const mod of catalogue.modules) {
      for (const sub of mod.submodules) {
        for (const ev of sub.events) {
          const now = views.get(ev.key);
          const before = savedViews.get(ev.key);
          if (now && before && !sameView(now, before, ev.clientEvent)) count += 1;
        }
      }
    }
    return count + (patch ? Object.keys(patch).length : 0);
  }, [catalogue, views, savedViews, patch]);

  const dirty = changeCount > 0;

  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const editDraft = useCallback((update: (current: Draft) => Draft) => {
    setSaveError(null);
    setDraft((current) => update(current));
  }, []);

  const discard = () => {
    if (!saved) return;
    setDraft(draftFromRows(saved.preferences));
    setSettings(saved.settings);
    setSaveError(null);
  };

  const save = async () => {
    if (!saved || !settings) return;
    setSaving(true);
    setSaveError(null);
    const body: NotificationPreferencesInput = { preferences: rowsFromDraft(draft) };
    if (patch) body.settings = patch;
    try {
      const response = await api.saveNotificationPreferences(body, targetUserId);
      setSaved(response.data);
      setDraft(draftFromRows(response.data.preferences));
      setSettings(response.data.settings);
    } catch (error) {
      setSaveError(
        error instanceof ApiError && error.code === 'NOTIFICATION_NO_ACCESS' && catalogue
          ? noAccessSaveText(error, body.preferences, catalogue, personName)
          : errorText(error),
      );
    } finally {
      setSaving(false);
    }
  };

  const confirmReset = async () => {
    if (!saved || !resetTarget) return;
    setResetting(true);
    setResetError(null);
    const mod = resetTarget;
    const rows = rowsFromDraft(withoutModule(draftFromRows(saved.preferences), mod));
    try {
      const response = await api.saveNotificationPreferences({ preferences: rows }, targetUserId);
      setSaved(response.data);
      /* Unsaved edits in other modules survive the reset. */
      setDraft((current) => withoutModule(current, mod));
      setResetTarget(null);
    } catch (error) {
      setResetError(
        error instanceof ApiError && error.code === 'NOTIFICATION_NO_ACCESS' && catalogue
          ? noAccessSaveText(error, rows, catalogue, personName)
          : errorText(error),
      );
    } finally {
      setResetting(false);
    }
  };

  const query = search.trim().toLowerCase();
  const searching = query !== '';

  const isVisibleIn = useCallback(
    (mod: NotificationModule, subLabel: string) => (ev: NotificationEventInfo) => {
      if (filter === 'changed' && views.get(ev.key)?.source !== 'user') return false;
      if (!searching) return true;
      return (
        ev.label.toLowerCase().includes(query) ||
        subLabel.toLowerCase().includes(query) ||
        mod.label.toLowerCase().includes(query)
      );
    },
    [filter, views, searching, query],
  );

  const visibleModules = useMemo(() => {
    if (!catalogue || !saved) return [];
    const shown = catalogue.modules.filter((mod) =>
      mod.submodules.some((sub) => sub.events.some(isVisibleIn(mod, sub.label))),
    );
    /* Stable: accessible modules keep catalogue order, no-access ones follow. */
    return [
      ...shown.filter((mod) => !moduleNoAccess(saved, mod)),
      ...shown.filter((mod) => moduleNoAccess(saved, mod)),
    ];
  }, [catalogue, saved, isVisibleIn]);

  const isOwn = targetUserId === null;
  const title = isOwn
    ? 'Notifications'
    : `Notifications · ${targetName ?? `User #${String(targetUserId)}`}`;

  const toggleModule = (key: string) =>
    setOpenModules((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  if (invalidUserId) {
    return (
      <>
        <PageHeader title="Notifications" />
        <Notice tone="danger" role="alert">
          The link does not name a user.
        </Notice>
      </>
    );
  }

  const loading = !loadError && (!catalogue || !saved || !settings);

  return (
    <>
      <PageHeader title={title} />

      {loadError && (
        <Notice
          tone="danger"
          role="alert"
          action={
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setLoadError(null);
                setReloadToken((value) => value + 1);
              }}
            >
              Retry
            </Button>
          }
        >
          {loadError}
        </Notice>
      )}

      {loading && (
        <div aria-busy="true" className="flex flex-col gap-3">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-9 w-full max-w-md" />
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} className="h-12 w-full" />
          ))}
        </div>
      )}

      {!loadError && catalogue && saved && settings && (
        <>
          <SettingsStrip
            settings={settings}
            disabled={saving}
            onChange={(next) => {
              setSaveError(null);
              setSettings(next);
            }}
          />

          {catalogue.modules.length === 0 ? (
            <Panel>
              <EmptyState title="No notification events are available yet." />
            </Panel>
          ) : (
            <>
              <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="w-full sm:max-w-sm">
                  <Input
                    type="search"
                    aria-label="Search events"
                    placeholder="Search"
                    leadingIcon={<SearchIcon />}
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                </div>
                <SegmentedControl
                  label="Filter events"
                  value={filter}
                  options={FILTER_OPTIONS}
                  onChange={setFilter}
                  className="sm:w-auto"
                />
              </div>

              {visibleModules.length === 0 ? (
                <Panel>
                  <EmptyState
                    title={filter === 'changed' && !searching ? 'No events changed by this person.' : 'No events match.'}
                    action={
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          setSearch('');
                          setFilter('all');
                        }}
                      >
                        Show all events
                      </Button>
                    }
                  />
                </Panel>
              ) : (
                <div className="flex flex-col gap-3">
                  {visibleModules.map((mod) => (
                    <ModuleCard
                      key={mod.key}
                      mod={mod}
                      views={views}
                      isVisible={(ev) => {
                        const sub = mod.submodules.find((candidate) => candidate.events.includes(ev));
                        return isVisibleIn(mod, sub?.label ?? '')(ev);
                      }}
                      collapsed={searching || filter === 'changed' ? false : !openModules.has(mod.key)}
                      onToggle={() => toggleModule(mod.key)}
                      disabled={saving || resetting}
                      canReset={moduleHasRows(saved.preferences, mod) || moduleHasRows(draft.values(), mod)}
                      noAccess={moduleNoAccess(saved, mod)}
                      noAccessReason={noAccessReason(mod.label, personName)}
                      onReset={() => {
                        setResetError(null);
                        setResetTarget(mod);
                      }}
                      onEventChannel={(ev, field: ChannelField, value) =>
                        editDraft((current) => setEventField(current, ev, field, value))
                      }
                      onEventFrequency={(ev, value) =>
                        editDraft((current) => setEventField(current, ev, 'frequency', value))
                      }
                      onEventScope={(ev, value) =>
                        editDraft((current) => setEventField(current, ev, 'scope', value))
                      }
                      onGroupChannel={(sub, field, value) =>
                        editDraft((current) => setGroupChannel(current, mod, sub, field, value, noAccessKeys))
                      }
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {(dirty || saveError) && (
            <div
              role="region"
              aria-label="Unsaved changes"
              className="sticky bottom-0 z-20 mt-4 flex min-w-0 flex-col gap-2 rounded-md border border-hairline-strong bg-surface-raised px-3 py-2 shadow-md"
            >
              {saveError && (
                <Notice tone="danger" role="alert">
                  {saveError}
                </Notice>
              )}
              <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-medium text-fg" aria-live="polite">
                  {changeCount === 1 ? '1 unsaved change' : `${changeCount} unsaved changes`}
                </span>
                <span className="flex flex-wrap gap-2">
                  <Button variant="ghost" size="sm" onClick={discard} disabled={saving || !dirty}>
                    Discard
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => void save()}
                    loading={saving}
                    disabled={!dirty}
                  >
                    Save
                  </Button>
                </span>
              </div>
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        open={resetTarget !== null}
        onClose={() => setResetTarget(null)}
        onConfirm={() => void confirmReset()}
        title={resetTarget ? `Reset ${resetTarget.label} to team defaults` : 'Reset to team defaults'}
        description={
          resetTarget
            ? `${isOwn ? 'Your' : `${targetName ?? 'This person'}'s`} own settings for every ${resetTarget.label} event are removed and team defaults apply again.`
            : null
        }
        confirmLabel="Reset"
        loading={resetting}
        error={resetError}
      />
    </>
  );
}
